import { t } from 'i18next';
import { FilterValueInputType } from '../../../common/components/FilterPanel/enum/FilterValueInputType';
import { Operation } from '../../../common/components/FilterPanel/enum/Operation';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { TypographyOverflow } from '../../../common/components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState } from '../../../common/controllers/PaginatedTableController';
import { withRouter, WithRouterProps } from '../../../common/higher-order-components/with-router';
import { FieldConfiguration } from '../../../common/types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { HostsService } from '../services/HostsService';
import { getScanPlatformLabel, ScanPlatform } from '../enums/ScanPlatform';
import { getHostStatusLabel, HostStatus } from '../enums/HostStatus';
import { Host } from '../models/Host';
import { BlankCell } from '../../../common/components/Cells/BlankCell';
import { Button } from '@mui/material';
import { PATHS } from '../../../routing/routes';
import { UrlHelper } from '../../../common/utils/UrlHelper';

type State = PaginatedTableState & {};

type Props = WithRouterProps<{}> & {
  scanId: number;
};

class Controller extends PaginatedTableController<Host, Props, State> {
  private readonly hostsService = new HostsService();

  componentWillUnmount() {
    super.componentWillUnmount();
    this.hostsService.cancelAllRequests();
  }

  protected getDefaultPageSize(): number {
    return 10;
  }

  protected shouldCheckPermissionToAccess(): boolean {
    return false;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [
      {
        filterKey: 'name',
        label: t('hostScan.name'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
      },
      {
        filterKey: 'status',
        label: t('hostScan.status'),
        type: FilterValueInputType.SELECT,
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
        options: [
          { value: HostStatus.PENDING, label: getHostStatusLabel(HostStatus.PENDING) },
          { value: HostStatus.SETTING_UP, label: getHostStatusLabel(HostStatus.SETTING_UP) },
          { value: HostStatus.SCANNING, label: getHostStatusLabel(HostStatus.SCANNING) },
          { value: HostStatus.RETRIEVING_TRACE, label: getHostStatusLabel(HostStatus.RETRIEVING_TRACE) },
          { value: HostStatus.RETRIEVED_TRACE, label: getHostStatusLabel(HostStatus.RETRIEVED_TRACE) },
          { value: HostStatus.DOWNLOADING_TRACE, label: getHostStatusLabel(HostStatus.DOWNLOADING_TRACE) },
          { value: HostStatus.DOWNLOADED_TRACE, label: getHostStatusLabel(HostStatus.DOWNLOADED_TRACE) },
          { value: HostStatus.SUCCESS, label: getHostStatusLabel(HostStatus.SUCCESS) },
          { value: HostStatus.FAIL, label: getHostStatusLabel(HostStatus.FAIL) },
        ],
      },
      {
        filterKey: 'platform',
        label: t('hostScan.platform'),
        type: FilterValueInputType.SELECT,
        operations: [
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
        options: [
          { value: ScanPlatform.LINUX, label: t('scanPlatforms.linux') },
          { value: ScanPlatform.WINDOWS, label: t('scanPlatforms.windows') },
        ],
      },
      {
        filterKey: 'originId',
        label: t('hostScan.originId'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
          { value: Operation.NEQ, label: t('filterLabels.isNot') },
        ],
      },
    ];
  }

  protected getFieldConfigurations(): FieldConfiguration<Host>[] {
    return [
      {
        fieldKey: 'name',
        sortable: true,
        gqlKeys: { sorterField: 'name' },
        label: t('hostScan.name'),
        description: t('hostScan.name'),
        render: (model, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{model.getName()}</TypographyOverflow>,
      },
      {
        fieldKey: 'status',
        sortable: true,
        gqlKeys: { sorterField: 'status' },
        label: t('hostScan.status'),
        description: t('hostScan.status'),
        render: (model, { hasFocus }) => {
          return <TypographyOverflow hasFocus={hasFocus}>{getHostStatusLabel(model.getStatus())}</TypographyOverflow>;
        },
      },
      {
        fieldKey: 'platform',
        sortable: true,
        gqlKeys: { sorterField: 'platform' },
        label: t('hostScan.platform'),
        description: t('hostScan.platform'),
        render: (model, { hasFocus }) => {
          return <TypographyOverflow hasFocus={hasFocus}>{getScanPlatformLabel(model.getPlatform())}</TypographyOverflow>;
        },
      },
      {
        fieldKey: 'statusReason',
        label: t('hostScan.statusReason'),
        description: t('hostScan.statusReason'),
        render: (model, { hasFocus }) => {
          const statusReason = model.getStatusReason();
          if (statusReason) {
            return <TypographyOverflow hasFocus={hasFocus}>{model.getStatusReason()}</TypographyOverflow>;
          }
          return <BlankCell blankText={t('none')} />;
        },
      },
      {
        fieldKey: 'originId',
        sortable: true,
        gqlKeys: { sorterField: 'originId' },
        label: t('hostScan.originId'),
        description: t('hostScan.originId'),
        render: (model, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{model.getOriginId()}</TypographyOverflow>,
      },
      {
        fieldKey: 'reportLink',
        sortable: false,
        label: t('hostScan.report'),
        description: t('hostScan.report'),
        render: (model, { hasFocus }) => {
          const reportId = model.getReportId();
          if (model.getStatus() === HostStatus.SUCCESS && reportId === null) {
            return <TypographyOverflow hasFocus={hasFocus}>{t('hostScan.reportDeleted')}</TypographyOverflow>;
          }

          if (reportId === null) {
            return '-';
          }

          const buttonProps = { href: UrlHelper.generatePath(PATHS.REPORT, { reportId: reportId.toString() }), 'aria-label': t('hostScan.report') };
          return (
            <Button variant="outlined" size={'small'} color="primary" {...buttonProps}>
              {t('hostScan.report')}
            </Button>
          );
        },
      },
    ];
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<Host>> {
    try {
      return await this.hostsService.getList(this.props.scanId, cursorInfo, this.getFilters(), this.getSorters());
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }
}

export const HostList = withRouter(Controller);
